import { TAxiosResponse } from 'types';
import { axiosInstance } from './axiosInstance';
import { TCommonError } from '@models/common';
import {
  TConfirmSealRequestReq,
  TSealRequestExportReq,
  TSealRequestExportResponse,
  TSealRequestListItem,
  TSealRequestListReq,
  TSealRequestResponse,
} from '@models/sealRequest';

/**
 * Service layer for VAMS_PC_008 (管理タグ要求).
 *
 * NOTE (Open Item — base URL prefix): most existing `src/services/*Api.ts` files in this repo call
 * relative paths without the `/api/v1` prefix (e.g. `storeApi.ts` → `/store/search-stores`), while
 * `authApi.ts` uses the full `/api/v1/auth/login` path. `axiosInstance.ts`'s `baseURL` does not add
 * this prefix itself (no proxy/rewrite config found in `craco.config.js`/`package.json`). Since
 * `api-integration.md` explicitly documents "Base URL: /api/v1" for these endpoints and
 * `authApi.ts` is the only other file that has been proven to work end-to-end with an explicit
 * `/api/v1` path, this service follows that convention. If the real deployed backend actually
 * expects the shorter path (matching the majority of other services), adjust the URLs below —
 * flagged here rather than silently guessing against the documented contract.
 */
const sealRequestApi = {
  /** GET /api/v1/assets/seal-requests — api-integration.md §2.1 (ADMIN-only, no pagination). */
  getSealRequests: async (payload?: TSealRequestListReq) => {
    const url = '/api/v1/assets/seal-requests';
    const response: TAxiosResponse<TSealRequestListItem[], TCommonError> =
      await axiosInstance.get(url, {
        params: {
          SessionCode: payload?.sessionCode || undefined,
          CompanyCode: payload?.companyCode || undefined,
          StoreId: payload?.storeId || undefined,
        },
      });
    return response;
  },

  /**
   * POST /api/v1/assets/seal-requests/export — エクスポート. Applies the same filters as
   * getSealRequests, plus an optional `ids` list to export only the checked rows (選択
   * column). The CSV is uploaded to S3 (seal-request/ folder) instead of being returned for
   * download — the response only carries the resulting bucket/key.
   */
  exportSealRequests: async (payload?: TSealRequestExportReq) => {
    const url = '/api/v1/assets/seal-requests/export';
    const response: TAxiosResponse<TSealRequestExportResponse, TCommonError> =
      await axiosInstance.post(url, {
        SessionCode: payload?.sessionCode || undefined,
        CompanyCode: payload?.companyCode || undefined,
        StoreId: payload?.storeId || undefined,
        Ids: payload?.ids?.length ? payload.ids : undefined,
      });
    return response;
  },

  /**
   * PATCH /api/v1/assets/seal-requests/{requestCode}/status — api-integration.md §2.3.
   * Caller MUST always pass `requestStatusCode: "SENT"` explicitly in `payload` (do not rely on
   * server-side fallback default).
   */
  confirmSealRequest: async (
    requestCode: string,
    payload: TConfirmSealRequestReq
  ) => {
    const url = `/api/v1/assets/seal-requests/${requestCode}/status`;
    const response: TAxiosResponse<TSealRequestResponse, TCommonError> =
      await axiosInstance.put(url, payload);
    return response;
  },
};

export { sealRequestApi };
